import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { TFormQuestion } from '@onesource/services';
import type { TSubmitFormAnswer } from '@onesource/schemas';
import { getAcceptCheckboxLabel } from '@onesource/schemas';

interface AcceptWithSignatureQuestionProps {
  question: TFormQuestion;
  answer: TSubmitFormAnswer;
  onAnswerChange: (answer: TSubmitFormAnswer) => void;
  disabled?: boolean;
}

interface SignatureValue {
  checked: boolean;
  name: string;
}

const parseSignatureValue = (textAnswer: string | undefined): SignatureValue => {
  if (!textAnswer) {
    return { checked: false, name: '' };
  }
  try {
    const parsed = JSON.parse(textAnswer) as unknown;
    if (parsed && typeof parsed === 'object' && 'checked' in parsed && 'name' in parsed) {
      return { checked: Boolean((parsed as SignatureValue).checked), name: String((parsed as SignatureValue).name) };
    }
  } catch {
    // ignore
  }
  return { checked: false, name: '' };
};

const isNameValid = (name: string) => name.trim().split(/\s+/).filter(Boolean).length >= 2;

export const AcceptWithSignatureQuestion = ({
  question,
  answer,
  onAnswerChange,
  disabled = false,
}: AcceptWithSignatureQuestionProps) => {
  const checkboxLabel = getAcceptCheckboxLabel(question.metadata);
  const { checked, name } = parseSignatureValue(answer.textAnswer);

  const [localName, setLocalName] = useState(name);

  useEffect(() => {
    setLocalName(parseSignatureValue(answer.textAnswer).name);
  }, [answer.textAnswer]);

  const commit = (nextChecked: boolean, nextName: string) => {
    onAnswerChange({
      ...answer,
      textAnswer: JSON.stringify({ checked: nextChecked, name: nextName }),
    });
  };

  const handleCheckedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    commit(e.target.checked, localName);
  };

  const handleNameBlur = () => {
    commit(checked, localName);
  };

  const showNameError = question.isRequired && localName.trim().length > 0 && !isNameValid(localName);

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={handleCheckedChange}
            disabled={disabled}
            sx={{ color: '#ff4d6d', '&.Mui-checked': { color: '#ff4d6d' } }}
          />
        }
        label={checkboxLabel}
        sx={{ mb: 2, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 1 } }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Type your full name to sign
      </Typography>
      <TextField
        fullWidth
        value={localName}
        onChange={e => setLocalName(e.target.value)}
        onBlur={handleNameBlur}
        disabled={disabled}
        placeholder="First Last"
        variant="outlined"
        size="small"
        error={showNameError}
        helperText={showNameError ? 'Please enter your full name (first and last)' : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fafafa',
            '& fieldset': { borderColor: '#ddd' },
            '&:hover fieldset': { borderColor: '#ff4d6d' },
            '&.Mui-focused fieldset': { borderColor: '#ff4d6d' },
          },
        }}
      />
    </Box>
  );
};
